import { CirclePause, LockKeyhole, ShieldAlert, Sparkles } from "lucide-react";
import { useState } from "react";
import { Badge, Card, InlineMessage, PageHeader, SectionHeading, Switch } from "../components/ui";
import { useWorkspace } from "../lib/workspace";

export function AutomationPage() {
  const { data, setPaused, setChannelAutoReply } = useWorkspace();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  if (!data) return null;

  async function togglePause(paused: boolean) {
    setBusy("pause");
    try {
      await setPaused(paused);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Could not save pause setting.');
    } finally {
      setBusy(null);
    }
  }

  async function toggleChannel(channelId: string, enabled: boolean) {
    setBusy(channelId);
    try {
      await setChannelAutoReply(channelId, enabled);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Could not save automation setting.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="Safe by default"
        title="Automation"
        description="Control what may send automatically. Drafting and incoming sync continue while outgoing sends are paused."
      />

      <Card className={`pause-card ${data.settings.sendsPaused ? "pause-card--active" : ""}`}>
        <div className="pause-card__icon"><CirclePause size={24} /></div>
        <div>
          <h2>Pause all sends</h2>
          <p>Stops new and pending outgoing work. Overdue jobs require a fresh review after you resume.</p>
        </div>
        <Switch
          checked={data.settings.sendsPaused}
          onChange={(checked) => void togglePause(checked)}
          label={data.settings.sendsPaused ? "Paused" : "Active"}
          disabled={busy === "pause"}
        />
      </Card>
      {error ? <InlineMessage tone="danger" title="Setting not saved">{error}</InlineMessage> : null}

      <div className="automation-grid">
        <Card>
          <SectionHeading
            title="Approved FAQ replies"
            description="Off by default. Only one exact approved question variant may match."
          />
          <InlineMessage tone="info" title="Human review remains the fallback">
            Unknown, ambiguous, multiple-intent, sensitive and clinical questions are always routed to review.
          </InlineMessage>
          <div className="channel-toggle-list">
            {data.channels.map((channel) => (
              <div className="channel-toggle" key={channel.id}>
                <div>
                  <span className={`provider-dot provider-dot--${channel.provider}`} />
                  <span><strong>{channel.name}</strong><small>{channel.handle}</small></span>
                </div>
                <Switch
                  checked={channel.autoReplyEnabled}
                  onChange={(checked) => void toggleChannel(channel.id, checked)}
                  label={channel.autoReplyEnabled ? "On" : "Off"}
                  disabled={busy === channel.id || channel.status !== "connected" || data.settings.sendsPaused}
                />
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <SectionHeading title="Non-negotiable safeguards" description="These rules cannot be switched off from the UI." />
          <div className="safeguard-list">
            <Safeguard icon={<LockKeyhole />} title="One-use approval" body="Approval is bound to one revision, channel, target, media set and dispatch time." />
            <Safeguard icon={<ShieldAlert />} title="Sensitive-question review" body="Clinical, emergency, privacy and payment concerns stay with a human." />
            <Safeguard icon={<Sparkles />} title="Untrusted incoming text" body="Customer text cannot edit business rules or activate sending tools." />
          </div>
        </Card>
      </div>

      <Card>
        <SectionHeading title="Approved FAQ library" description={`${data.context.faqs.filter((faq) => faq.approved && faq.active).length} active approved answers`} />
        <div className="faq-table">
          {data.context.faqs.map((faq) => (
            <div className="faq-row" key={faq.id}>
              <div><strong lang="my">{faq.question}</strong><p lang="my">{faq.answer}</p></div>
              <span>{faq.variants.length} variants</span>
              <Badge tone={faq.sensitive ? "red" : faq.approved ? "green" : "amber"}>
                {faq.sensitive ? "Review only" : faq.approved ? "Approved" : "Draft"}
              </Badge>
            </div>
          ))}
        </div>
      </Card>
    </>
  );
}

function Safeguard({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return <div className="safeguard"><span>{icon}</span><div><strong>{title}</strong><p>{body}</p></div></div>;
}
